// Stok kartı formunun tüm durumu ve kayıt mantığı.
// Eski AddInventoryItem.tsx gövdesinden davranış değiştirmeden taşındı; ekran bölümleri bu nesneyi kullanır.
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../../../contexts/AuthContext';
import { UNIT_OPTIONS } from '../../../../constants/units';
import { type PackagingEditorHandle } from './packaging/PackagingEditor';
import { PackagingError, describeConflict, resolveBarcodes } from '../services/packagingService';
import { InventoryService, CreateInventoryItemDto } from '../services/inventoryService';
import { CategoryService } from '../services/categoryService';
import { BrandService } from '../services/brandService';
import { WarehouseService } from '../services/warehouseService';
import {
  ProductFeatureService,
  type InventoryProductFeatureDto,
  type SaveInventoryItemFeatureValueRowDto
} from '../services/productFeatureService';
import { smartWarehouseService } from '../../../wms/services/smartWarehouseService';
import { accountingApi } from '../../../../config/apiClient';
import { ACCOUNTING_API } from '../../../../config/api';
import { generateInternalBarcode } from '../../../../utils/ean13';
import { useTranslation } from 'react-i18next';
import {
  type AddInventoryItemProps,
  type InventoryCategory,
  type Warehouse,
  type Brand,
  type LocalBranch,
  type ExtraFeatureRow,
  type InventoryItem,
  normalizeImageUrls,
} from './types';

export function useStockCardForm(props: AddInventoryItemProps) {
  const { open, onClose, branchId, itemToEdit, onItemAdded, onItemUpdated, prefillName, prefill } = props;
  const { t } = useTranslation('accounting');
  // ✅ Düzenleme modu kontrolü
  const isEditMode = Boolean(itemToEdit);
  
  // ✅ Merkezi Cache Sistemi - AuthContext'ten al (global branches ve userDetails)
  const { branches: globalBranches, userDetails, isModuleEnabled } = useAuth();
  const productionEnabled = isModuleEnabled('production');
  const lotTrackingEnabled = isModuleEnabled('lot_tracking');
  
  // 🔍 OFFLINE DEBUG
  const hasLoggedRef = React.useRef(false);
  React.useEffect(() => {
    if (open && !hasLoggedRef.current) {
      hasLoggedRef.current = true;
      console.log('🚀 [ADD-ITEM] Modal açıldı');
      console.log('🔍 [ADD-ITEM] AuthContext userDetails:', userDetails ? `✅ companyId: ${userDetails.companyId}` : '❌ YOK');
    }
    if (!open) {
      hasLoggedRef.current = false;
    }
  }, [open, userDetails]);
  
  // Hızlı ekle penceresinden "Tam karta geç" ile gelen değerler
  React.useEffect(() => {
    if (!open || isEditMode || !prefill) return;
    if (prefill.name !== undefined) setItemName(prefill.name);
    if (prefill.barcode !== undefined) setBarcode(prefill.barcode);
    if (prefill.unit) setUnit(prefill.unit);
    if (prefill.vatRate !== undefined) setVatRate(prefill.vatRate);
    if (prefill.vatStatus !== undefined) setVatStatus(prefill.vatStatus);
    if (prefill.salePrice !== undefined) setSalePrice(prefill.salePrice);
    if (prefill.purchasePrice !== undefined) setPurchasePrice(prefill.purchasePrice);
    if (prefill.categoryId !== undefined) setCategoryId(prefill.categoryId);
    if (prefill.departmentNo !== undefined) setDepartmentNo(prefill.departmentNo);
    if (prefill.itemType) setItemType(prefill.itemType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, prefill, isEditMode]);

  // ✅ YENİ: E-fatura'dan gelen ürün adını otomatik doldur
  React.useEffect(() => {
    if (open && prefillName && !isEditMode) {
      console.log('🏷️ [ADD-ITEM] E-fatura ürün adı otomatik dolduruluyor:', prefillName);
      setItemName(prefillName);
    }
  }, [open, prefillName, isEditMode]);

  // ✅ YENİ: Düzenleme modu - itemToEdit geldiğinde form alanlarını doldur
  React.useEffect(() => {
    if (open && itemToEdit && isEditMode) {
      console.log('📝 [ADD-ITEM] Düzenleme modu - form dolduruluyor:', itemToEdit);
      
      // Temel bilgiler
      setItemName(itemToEdit.name || '');
      setBarcode(itemToEdit.barcode || '');
      setIsDomesticProduct((itemToEdit as any).isDomesticProduct === true);
      setCountryOfOrigin((itemToEdit as any).countryOfOrigin || '');
      setIsScale((itemToEdit as any).isScale === true);
      setScaleCode((itemToEdit as any).scaleCode || '');
      setShelfLifeDays(
        (itemToEdit as any).shelfLifeDays ? String((itemToEdit as any).shelfLifeDays) : ''
      );
      setTrackLots((itemToEdit as any).trackLots === true || (itemToEdit as any).TrackLots === true);
      setTrackExpiry((itemToEdit as any).trackExpiry === true || (itemToEdit as any).TrackExpiry === true);
      setStockCode(itemToEdit.stockCode || '');
      setCategoryId(itemToEdit.categoryId || null);
      setBrandId(itemToEdit.brandId || null);
      setUnit(itemToEdit.unit || 'adet');
      setDescription(itemToEdit.description || '');
      setBranchesId(itemToEdit.branchesId || null);
      setImageUrls(normalizeImageUrls((itemToEdit as any).imageUrl, (itemToEdit as any).imageUrls));
      
      // ⚠️ KRİTİK: Stok eksiye düşebilir ayarı
      // Backend'den canBeNegative veya CanBeNegative olarak gelebilir
      const canBeNegativeValue = itemToEdit.canBeNegative ?? itemToEdit.CanBeNegative ?? true;
      setCanBeNegative(canBeNegativeValue);
      console.log('🔒 [ADD-ITEM] canBeNegative:', canBeNegativeValue, '(raw:', itemToEdit.canBeNegative, itemToEdit.CanBeNegative, ')');

      setIsVariantParent(itemToEdit.isVariantParent === true);
      
      // Fiyat bilgileri (Temel)
      setPurchasePrice(itemToEdit.purchasePrice || '');
      setPurchaseCurrency(itemToEdit.purchaseCurrency || 'TRY');
      setPurchaseVendor(itemToEdit.purchaseVendor || '');
      setSalePrice(itemToEdit.salePrice || '');
      setSaleCurrency(itemToEdit.saleCurrency || 'TRY');
      setSaleCustomer(itemToEdit.saleCustomer || '');
      
      // ✅ Ek Fiyat Bilgileri (purchasePrice2, purchasePrice3, listPrice, vb.)
      setPurchasePrice2(itemToEdit.purchasePrice2 || '');
      setPurchaseCurrency2(itemToEdit.purchaseCurrency2 || 'TRY');
      setPurchaseVendor2(itemToEdit.purchaseVendor2 || '');
      setPurchasePrice3(itemToEdit.purchasePrice3 || '');
      setPurchaseCurrency3(itemToEdit.purchaseCurrency3 || 'TRY');
      setPurchaseVendor3(itemToEdit.purchaseVendor3 || '');
      
      setListPrice((itemToEdit as any).listPrice || '');
      setListCurrency((itemToEdit as any).listCurrency || 'TRY');
      
      setSalePrice2(itemToEdit.salePrice2 || '');
      setSaleCurrency2(itemToEdit.saleCurrency2 || 'TRY');
      setSaleCustomer2(itemToEdit.saleCustomer2 || '');
      setSalePrice3(itemToEdit.salePrice3 || '');
      setSaleCurrency3(itemToEdit.saleCurrency3 || 'TRY');
      setSaleCustomer3(itemToEdit.saleCustomer3 || '');
      
      // Vergi bilgileri
      // FIX: `||` yerine `??` kullanılıyor: `||` operatörü 0 değerini falsy sayıp sessizce 20'ye çeviriyordu;
      //      `??` sadece null/undefined'da default'a düşer, gerçek 0 oranı korunur. Boş kayıtlarda eski
      //      davranışla uyumlu olarak %20 önerilir (Türkiye standart KDV).
      // FIX: vatStatus için orijinal default 'INCLUSIVE' (uppercase) idi; dropdown ve backend lowercase
      //      'included'/'excluded' kullanıyor, bu yüzden hiçbir <MenuItem>'a eşleşmiyor ve dropdown boş
      //      görünüyordu. Boş gelirse boş bırakıyoruz, zorunlu alan olarak kullanıcı doldurur.
      setVatRate(itemToEdit.vatRate ?? 20);
      setVatStatus(itemToEdit.vatStatus ?? '');
      setSctRate(itemToEdit.sctRate ?? 0);
      setSctStatus(itemToEdit.sctStatus ?? '');
      setDepartmentNo(itemToEdit.departmentNo || '');
      setItemType(itemToEdit.itemType === 'SERVICE' ? 'SERVICE' : 'PRODUCT');
      setIsActive((itemToEdit.isActive ?? itemToEdit.IsActive) !== false);
      setItemClass(itemToEdit.itemClass ?? itemToEdit.ItemClass ?? '');
      setProductionMode((itemToEdit.productionMode ?? itemToEdit.ProductionMode) === 'MAKE_TO_STOCK' ? 'MAKE_TO_STOCK' : 'SALE_EXPLODE');
      setVariantDesi(itemToEdit.desi || '');
      
      
      // Depo stokları: ilk kaydı kör seçmek yerine en yüksek pozitif miktarı seç.
      if (itemToEdit.warehouseStocks && itemToEdit.warehouseStocks.length > 0) {
        const stocks = [...itemToEdit.warehouseStocks];
        const positiveStocks = stocks
          .filter((s: any) => Number(s.quantity ?? 0) > 0)
          .sort((a: any, b: any) => Number(b.quantity ?? 0) - Number(a.quantity ?? 0));
        const positiveWithLocation = positiveStocks.filter((s: any) => !!s.locationId);
        const primaryStock =
          positiveWithLocation[0] ||
          positiveStocks[0] ||
          stocks[0];

        setWarehouseId(primaryStock.warehouseId ?? null);
        setQuantity(primaryStock.quantity ?? '');
        setSelectedLocationId(primaryStock.locationId ?? null);
      }
    }
  }, [open, itemToEdit, isEditMode]);
  
  // ✅ LocalBranch type'a dönüştürme (address ve isHeadquarter için)
  const branches: LocalBranch[] = React.useMemo(() => {
    return globalBranches.map(b => ({
      id: b.id,
      name: b.name,
      address: '',
      isHeadquarter: false
    }));
  }, [globalBranches]);
  
  // ✅ "Kaydet ve Yeni Ekle" sonrası başarı bildirimi (modal açık kaldığı için)
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [unitNotice, setUnitNotice] = useState<{ message: string; severity: 'info' | 'error' } | null>(null);
  const [baseUnitLocked, setBaseUnitLocked] = useState(false);
  const unitsEditorRef = useRef<PackagingEditorHandle>(null);
  // Ana barkod başka bir kartın koli/paket ya da ek barkoduysa açıklaması (yoksa null).
  const [barcodePackageOwner, setBarcodePackageOwner] = useState<string | null>(null);

  // Form state
  const [itemName, setItemName] = useState('');
  const [barcode, setBarcode] = useState('');
  const [isDomesticProduct, setIsDomesticProduct] = useState<boolean>(false);
  const [countryOfOrigin, setCountryOfOrigin] = useState<string>('');
  // Tartı: kod barkodun ortasındaki 5 hane, baştaki sıfırlar anlamlı
  const [isScale, setIsScale] = useState<boolean>(false);
  const [scaleCode, setScaleCode] = useState<string>('');
  const [shelfLifeDays, setShelfLifeDays] = useState<string>('');
  const [trackLots, setTrackLots] = useState(false);
  const [trackExpiry, setTrackExpiry] = useState(false);

  // Girilen gün sayısının etikette hangi tarihe denk geldiğini gösterir.
  // Cihaz "tartım günü + (değer - 1)" hesapladığı için 1 = bugün.
  const shelfLifeDaysHelperText = React.useMemo(() => {
    const days = parseInt(shelfLifeDays, 10);
    if (!Number.isFinite(days) || days <= 0) {
      return 'Boş bırakılırsa etikete son tüketim tarihi basılmaz.';
    }
    const target = new Date();
    target.setDate(target.getDate() + days - 1);
    return `Bugün dahil ${days} gün — bugün tartılsa etikette ${target.toLocaleDateString('tr-TR')} yazar.`;
  }, [shelfLifeDays]);

  // Tartı kodu iki işi birlikte yapıyor: ağırlık barkodunun ortasına basılan
  // kod ve tartıda ürünü çağırma numarası (PLU). Aynı sayı olduğu için
  // kullanıcıya ikisini birlikte söylüyoruz.
  const scaleCodeHelperText = React.useMemo(() => {
    const digits = scaleCode.replace(/\D/g, '');
    if (!digits) {
      return 'Boş bırakılırsa tartıya gönderimde otomatik atanır.';
    }
    const plu = parseInt(digits, 10);
    const padded = digits.padStart(5, '0');
    return `Tartıda PLU ${plu} olarak kaydedilir; barkodun ortasına ${padded} basılır.`;
  }, [scaleCode]);
  const [stockCode, setStockCode] = useState('');
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [brandId, setBrandId] = useState<string | null>(null);
  const [quantity, setQuantity] = useState<number | string>('');
  const [canBeNegative, setCanBeNegative] = useState<boolean>(true);
  const [isVariantParent, setIsVariantParent] = useState<boolean>(false);
  const [unit, setUnit] = useState('adet');
  const [warehouseId, setWarehouseId] = useState<string | null>(null);
  const [imageUrls, setImageUrls] = useState<string[]>([]);
  const [purchasePrice, setPurchasePrice] = useState<number | ''>( '');
  const [purchaseCurrency, setPurchaseCurrency] = useState('TRY');
  const [purchaseVendor, setPurchaseVendor] = useState('');
  const [purchasePrice2, setPurchasePrice2] = useState<number | ''>('');
  const [purchaseCurrency2, setPurchaseCurrency2] = useState('TRY');
  const [purchaseVendor2, setPurchaseVendor2] = useState('');
  const [purchasePrice3, setPurchasePrice3] = useState<number | ''>('');
  const [purchaseCurrency3, setPurchaseCurrency3] = useState('TRY');
  const [purchaseVendor3, setPurchaseVendor3] = useState('');
  const [salePrice, setSalePrice] = useState<number | ''>('');
  const [saleCurrency, setSaleCurrency] = useState('TRY');
  const [saleCustomer, setSaleCustomer] = useState('');
  const [listPrice, setListPrice] = useState<number | ''>('');
  const [listCurrency, setListCurrency] = useState('TRY');
  const [salePrice2, setSalePrice2] = useState<number | ''>('');
  const [saleCurrency2, setSaleCurrency2] = useState('TRY');
  const [saleCustomer2, setSaleCustomer2] = useState('');
  const [salePrice3, setSalePrice3] = useState<number | ''>('');
  const [saleCurrency3, setSaleCurrency3] = useState('TRY');
  const [saleCustomer3, setSaleCustomer3] = useState('');
  const [vatRate, setVatRate] = useState<number | ''>('');
  const [vatStatus, setVatStatus] = useState('');
  const [sctRate, setSctRate] = useState<number | ''>('');
  const [sctStatus, setSctStatus] = useState('excluded'); // excluded or included
  const [description, setDescription] = useState('');
  const [departmentNo, setDepartmentNo] = useState<number | ''>('');
  const [itemType, setItemType] = useState<'PRODUCT' | 'SERVICE'>('PRODUCT');
  const [isActive, setIsActive] = useState<boolean>(true);
  const [itemClass, setItemClass] = useState('');
  const [productionMode, setProductionMode] = useState<'SALE_EXPLODE' | 'MAKE_TO_STOCK'>('SALE_EXPLODE');
  
  const [variantLength, setVariantLength] = useState<number | ''>('');
  const [variantWidth, setVariantWidth] = useState<number | ''>('');
  const [variantHeight, setVariantHeight] = useState<number | ''>('');
  const [variantDesi, setVariantDesi] = useState<number | ''>('');
  const [showDesiCalculator, setShowDesiCalculator] = useState<boolean>(false);
  
  // Şube seçimi için state
  const [selectedBranch, setSelectedBranch] = useState<LocalBranch | null>(null);
  const [branchesId, setBranchesId] = useState<string | null>(null);

  // Birim seçenekleri
  const unitOptions = [...UNIT_OPTIONS];
  const UNIT_TRANSLATION_KEYS: Record<string, string> = {
    adet: 'piece',
    kg: 'kg',
    gr: 'gram',
    lt: 'liter',
    ml: 'milliliter',
    m: 'meter',
    cm: 'centimeter',
    'm²': 'squareMeter',
    'm³': 'cubicMeter',
    'çift': 'pair',
    paket: 'pack',
    kutu: 'box',
    top: 'rollBundle',
    rulo: 'roll',
  };
  const getUnitLabel = (unitValue: string): string => {
    const key = UNIT_TRANSLATION_KEYS[unitValue];
    return key ? t(`stock.addModal.units.${key}`) : unitValue;
  };


  // Veritabanı verileri için state'ler
  const [categories, setCategories] = useState<InventoryCategory[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<InventoryCategory | null>(null);
  const [selectedWarehouse, setSelectedWarehouse] = useState<Warehouse | null>(null);
  const [selectedBrand, setSelectedBrand] = useState<Brand | null>(null);
  
  // Yükleme durumları
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [showInfoBox, setShowInfoBox] = useState(false);
  
  // Para birimi seçenekleri için state
  const [availableCurrencies, setAvailableCurrencies] = useState<{code: string, active: boolean}[]>([
    { code: 'TRY', active: true } // Varsayılan olarak TRY her zaman olacak
  ]);

  // Modal açılış durumlarını kontrol eden state'ler
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [isBrandModalOpen, setIsBrandModalOpen] = useState(false);
  const [isWarehouseModalOpen, setIsWarehouseModalOpen] = useState(false);
  const [isImageLibraryModalOpen, setIsImageLibraryModalOpen] = useState(false);
  const [hasExtraFeatures, setHasExtraFeatures] = useState(false);
  const [availableFeatures, setAvailableFeatures] = useState<InventoryProductFeatureDto[]>([]);
  const [itemFeatureValues, setItemFeatureValues] = useState<ExtraFeatureRow[]>([]);

  // Barkod kontrolü için yeni state ekleyelim
  const [barcodeExists, setBarcodeExists] = useState(false);
  const [existingItem, setExistingItem] = useState<InventoryItem | null>(null);
  const [isDuplicateBarcodeModalOpen, setIsDuplicateBarcodeModalOpen] = useState(false);
  
  // Props olarak gelen branchId varsa modal açıldığında otomatik seç
  useEffect(() => {
    if (open && branchId) {
      setBranchesId(branchId);
    }
  }, [open, branchId]);

  // Şirket ID'sini ve verileri yükleme
  useEffect(() => {
    if (open) {
      setShowInfoBox(false);
      // ✅ Varsayılan değerleri SADECE YENİ ÜRÜN modunda ayarla
      // Düzenleme modunda itemToEdit'ten değerler gelecek
      if (!itemToEdit && !prefill) {
        setUnit('adet');
        setVatRate('');
        setVatStatus('');
      }
      
      console.log('🔄 [ADD-ITEM] companyId ayarlanıyor...');
      
      // AuthContext'ten companyId al
      if (userDetails?.companyId) {
        console.log('✅ [ADD-ITEM] AuthContext\'ten companyId alındı:', userDetails.companyId);
        setCompanyId(userDetails.companyId);
        loadCurrencies(userDetails.companyId);
        setInitialLoading(false);
      } else {
        console.warn('⚠️ [ADD-ITEM] AuthContext\'te companyId yok, henüz yüklenmemiş olabilir');
        setInitialLoading(false);
      }
    }
    
    // Cleanup - Artık Firebase listener yok
    return () => {};
  }, [open, userDetails?.companyId]);

  useEffect(() => {
    const loadFeatureDefinitions = async () => {
      if (!open || !companyId) return;
      try {
        const features = await ProductFeatureService.getAll();
        setAvailableFeatures(Array.isArray(features) ? features : []);
      } catch (featureError) {
        console.error('❌ [ADD-ITEM] Ek özellik tanımları yüklenemedi:', featureError);
      }
    };

    loadFeatureDefinitions();
  }, [open, companyId]);

  useEffect(() => {
    const loadItemFeatureValues = async () => {
      if (!open) return;

      if (isEditMode && itemToEdit?.id) {
        try {
          const values = await ProductFeatureService.getValuesByItemId(itemToEdit.id);
          if (values.length > 0) {
            setHasExtraFeatures(true);
            setItemFeatureValues(values.map((v, index) => ({
              tempId: `${v.id || index}-${v.featureId}`,
              featureId: v.featureId,
              value: v.value || '',
            })));
          } else {
            setHasExtraFeatures(false);
            setItemFeatureValues([]);
          }
        } catch (featureValueError) {
          console.error('❌ [ADD-ITEM] Ürün ek özellik değerleri yüklenemedi:', featureValueError);
          setHasExtraFeatures(false);
          setItemFeatureValues([]);
        }
      } else if (!isEditMode) {
        setHasExtraFeatures(false);
        setItemFeatureValues([]);
      }
    };

    loadItemFeatureValues();
  }, [open, isEditMode, itemToEdit?.id]);

  // Şube değiştiğinde kategorileri, markaları ve depoları yeniden yükle
  useEffect(() => {
    if (companyId && open) {
      loadCategories(companyId);
      loadWarehouses(companyId);
      loadBrands(companyId);
    }
  }, [branchesId, companyId, open]);

  // ✅ YENİ: Kategoriler yüklendiğinde ve categoryId varsa, seçili kategoriyi bul
  useEffect(() => {
    if (categories.length > 0 && categoryId && (isEditMode || !!prefill?.categoryId)) {
      const category = categories.find(c => c.id === categoryId);
      if (category) {
        setSelectedCategory(category);
        console.log('✅ [ADD-ITEM] Kategori eşleştirildi:', category.name);
      } else {
        console.warn('⚠️ [ADD-ITEM] Kategori bulunamadı:', categoryId);
      }
    }
  }, [categories, categoryId, isEditMode]);

  // ✅ YENİ: Markalar yüklendiğinde ve brandId varsa, seçili markayı bul
  useEffect(() => {
    if (brands.length > 0 && brandId && isEditMode) {
      const brand = brands.find(b => b.id === brandId);
      if (brand) {
        setSelectedBrand(brand);
        console.log('✅ [ADD-ITEM] Marka eşleştirildi:', brand.name);
      } else {
        console.warn('⚠️ [ADD-ITEM] Marka bulunamadı:', brandId);
      }
    }
  }, [brands, brandId, isEditMode]);

  // ✅ YENİ: Depolar yüklendiğinde ve warehouseId varsa, seçili depoyu bul
  useEffect(() => {
    if (warehouses.length > 0 && warehouseId && isEditMode) {
      const warehouse = warehouses.find(w => w.id === warehouseId);
      if (warehouse) {
        setSelectedWarehouse(warehouse);
        console.log('✅ [ADD-ITEM] Depo eşleştirildi:', warehouse.name);
      } else {
        console.warn('⚠️ [ADD-ITEM] Depo bulunamadı:', warehouseId);
      }
    }
  }, [warehouses, warehouseId, isEditMode]);

  // ✅ YENİ: Şubeler yüklendiğinde ve branchesId varsa, seçili şubeyi bul
  useEffect(() => {
    if (branches.length > 0 && branchesId && isEditMode) {
      const branch = branches.find(b => b.id === branchesId);
      if (branch) {
        setSelectedBranch(branch);
        console.log('✅ [ADD-ITEM] Şube eşleştirildi:', branch.name);
      } else {
        console.warn('⚠️ [ADD-ITEM] Şube bulunamadı:', branchesId);
      }
    }
  }, [branches, branchesId, isEditMode]);

  // 🔒 Miktar değiştiğinde depo seçimini kontrol et
  useEffect(() => {
    // Düzenleme modunda mevcut ürünün deposunu otomatik değiştirme.
    if (isEditMode) return;

    const quantityValue = quantity === '' ? 0 : Number(quantity);
    
    if (quantityValue > 0) {
      // Miktar > 0 ise Merkez Depo'yu otomatik seç
      if (warehouses.length > 0 && !selectedWarehouse) {
        const defaultWarehouse = warehouses.find(w => w.isDefault) || warehouses[0];
        setSelectedWarehouse(defaultWarehouse);
        setWarehouseId(defaultWarehouse.id);
        console.log('🔒 Miktar > 0, Merkez Depo otomatik seçildi:', defaultWarehouse.name);
      }
    } else {
      // Miktar = 0 ise depo seçimini temizle
      setSelectedWarehouse(null);
      setWarehouseId(null);
      console.log('ℹ️ Miktar = 0, Depo seçimi temizlendi');
    }
  }, [quantity, warehouses, isEditMode, selectedWarehouse]);

  // NOT: Firebase fallback kaldırıldı - AuthContext'ten companyId alınıyor

  // ✅ branchId parametresi geldiğinde ve branches yüklendiğinde otomatik seç
  useEffect(() => {
    if (open && branchId && branches.length > 0 && !branchesId) {
      const selectedBranchObject = branches.find(branch => branch.id === branchId);
      if (selectedBranchObject) {
        setSelectedBranch(selectedBranchObject);
        setBranchesId(branchId);
        console.log(`Parametre ile gelen şube otomatik seçildi: ${selectedBranchObject.name}`);
      }
    }
    setInitialLoading(false);
  }, [open, branches, branchId, branchesId]);

  // Şube seçim işleyicisi
  const handleBranchChange = (value: LocalBranch | null) => {
    setSelectedBranch(value);
    setBranchesId(value ? value.id : null);
    
    // Şube değiştiğinde seçili kategori, marka ve depoyu sıfırla
    setSelectedCategory(null);
    setCategoryId(null);
    setSelectedBrand(null);
    setBrandId(null);
    setSelectedWarehouse(null);
    setWarehouseId(null);
  };

  // Şube adını bul
  const getBranchName = (branchId: string | null): string => {
    if (!branchId) return 'Tüm Şubeler';
    const branch = branches.find(b => b.id === branchId);
    return branch ? branch.name : 'Bilinmeyen Şube';
  };

  // Kategorileri yükleme (SQL API)
  const loadCategories = async (companyId: string) => {
    try {
      console.log('📦 SQL API\'den kategoriler yükleniyor...');
      const categoriesData = await CategoryService.getAllCategories(companyId);
      
      // SQL'den gelen kategorileri frontend interface'ine dönüştür
      const categoriesList: InventoryCategory[] = categoriesData.map(cat => ({
        id: cat.id,
        name: cat.name,
        parentId: cat.parentId || null,
        level: cat.level,
        createdAt: cat.createdAt,
        branchesId: null // SQL'de branch yok, Firebase'den alınacak
      }));
      
      // Filtreleme: Şube seçiliyse sadece o şubeye ait veya genel kategorileri göster
      let filteredCategories = categoriesList;
      if (branchesId) {
        filteredCategories = categoriesList.filter(category => 
          category.branchesId === branchesId || category.branchesId === null
        );
      }
      
      // Verileri seviyeye göre sıralayalım
      filteredCategories.sort((a, b) => {
        if (a.level !== b.level) {
          return a.level - b.level;
        }
        return a.name.localeCompare(b.name);
      });
      
      setCategories(filteredCategories);
      console.log('✅ SQL API\'den kategoriler yüklendi:', filteredCategories.length);
      
      // Seçili kategori artık filtrelenmiş listede yoksa, seçimi temizle
      if (selectedCategory && !filteredCategories.some(category => category.id === selectedCategory.id)) {
        setSelectedCategory(null);
        setCategoryId(null);
      }
    } catch (error) {
      console.error('❌ SQL API kategori yükleme hatası:', error);
    } finally {
      setInitialLoading(false);
    }
  };

  // Depoları yükleme (SQL API)
  // 🔒 Miktar girildiğinde MERKEZ DEPO otomatik seçilir
  const loadWarehouses = async (companyId: string) => {
    try {
      console.log('📦 SQL API\'den depolar yükleniyor...');
      const [warehousesResult, smartWarehousesResult] = await Promise.allSettled([
        WarehouseService.getAllWarehouses(companyId),
        smartWarehouseService.getAllList()
      ]);
      const warehousesData =
        warehousesResult.status === 'fulfilled' ? warehousesResult.value : [];
      const smartWarehouses =
        smartWarehousesResult.status === 'fulfilled' ? smartWarehousesResult.value : [];
      
      // SQL'den gelen depoları frontend interface'ine dönüştür
      const warehousesList: Warehouse[] = warehousesData.map(wh => ({
        id: wh.id,
        name: wh.name,
        address: wh.address || '',
        description: wh.description || '',
        isDefault: wh.isDefault || false,
        branchesId: null, // SQL'de branch yok
        warehouseType: wh.warehouseType || 'Normal',
      }));

      // Faz 5: Smart depo endpoint'inden gelenleri de birlestir.
      // Bazi ortamlarda inventory warehouse listesi sadece varsayilan depoyu donebildigi icin
      // smart depolari kaybetmemek adina iki kaynagi birlestiriyoruz.
      const existingIds = new Set(warehousesList.map(w => w.id));
      const mergedWarehouses: Warehouse[] = [...warehousesList];
      smartWarehouses.forEach((smartWh) => {
        if (!existingIds.has(smartWh.id)) {
          mergedWarehouses.push({
            id: smartWh.id,
            name: smartWh.warehouseName,
            address: '',
            description: '',
            isDefault: false,
            branchesId: null,
            warehouseType: 'Smart',
          });
          existingIds.add(smartWh.id);
        }
      });

      // Faz 5: Normal + Akıllı depolar birlikte listelensin (varsayılan depo üstte).
      const sortedWarehouses = [...mergedWarehouses].sort((a, b) => {
        if (a.isDefault && !b.isDefault) return -1;
        if (!a.isDefault && b.isDefault) return 1;
        return a.name.localeCompare(b.name);
      });

      setWarehouses(sortedWarehouses);
      console.log('✅ Depolar yüklendi:', sortedWarehouses.length);
    } catch (error) {
      console.error('❌ SQL API depo yükleme hatası:', error);
    }
  };

  // Markaları yükleme (SQL API)
  const loadBrands = async (companyId: string) => {
    try {
      console.log('📦 SQL API\'den markalar yükleniyor...');
      const brandsData = await BrandService.getAllBrands(companyId);
      
      // SQL'den gelen markaları frontend interface'ine dönüştür
      const brandsList: Brand[] = brandsData.map(br => ({
        id: br.id,
        name: br.name,
        description: br.description || '',
        website: '', // SQL'de website yok
        branchesId: null // SQL'de branch yok
      }));
      
      // Filtreleme: Şube seçiliyse sadece o şubeye ait veya genel markaları göster
      let filteredBrands = brandsList;
      if (branchesId) {
        filteredBrands = brandsList.filter(brand => 
          brand.branchesId === branchesId || brand.branchesId === null
        );
      }
      
      filteredBrands.sort((a, b) => a.name.localeCompare(b.name));
      
      setBrands(filteredBrands);
      console.log('✅ SQL API\'den markalar yüklendi:', filteredBrands.length);
      
      // Seçili marka artık filtrelenmiş listede yoksa, seçimi temizle
      if (selectedBrand && !filteredBrands.some(brand => brand.id === selectedBrand.id)) {
        setSelectedBrand(null);
        setBrandId(null);
      }
    } catch (error) {
      console.error('❌ SQL API marka yükleme hatası:', error);
    }
  };

  // Para birimlerini yükle (SQL API'den)
  const loadCurrencies = async (companyId: string) => {
    try {
      console.log('🔄 [ADD-ITEM] SQL API\'den para birimleri yükleniyor...');
      
      const response = await accountingApi.get(`/api/currency/settings`);
      
      const currencies: {code: string, active: boolean}[] = [
        { code: 'TRY', active: true }
      ];
      
      const result = response.data;
      
      if (result.success && result.data) {
        Object.entries(result.data).forEach(([code, active]: [string, any]) => {
          if (code !== 'TRY' && active === true) {
            currencies.push({ code, active: true });
          }
        });
      }
      
      console.log('✅ [ADD-ITEM] Aktif para birimleri yüklendi:', currencies.length);
      setAvailableCurrencies(currencies);
      
      // Eğer seçili para birimleri artık mevcut değilse, TRY'ye ayarla
      const purchaseCurrencyExists = currencies.some(c => c.code === purchaseCurrency && c.active);
      const saleCurrencyExists = currencies.some(c => c.code === saleCurrency && c.active);
      
      if (!purchaseCurrencyExists) {
        setPurchaseCurrency('TRY');
      }
      
      if (!saleCurrencyExists) {
        setSaleCurrency('TRY');
      }
      
    } catch (error) {
      console.error('❌ [ADD-ITEM] Para birimi yükleme hatası:', error);
      // Offline fallback: Varsayılan TRY kullan
      setAvailableCurrencies([{ code: 'TRY', active: true }]);
    }
  };

  // ✅ YENİ: Güncelleme fonksiyonu
  const handleUpdate = async () => {
    console.log('🔍 [DEBUG] itemToEdit:', itemToEdit);
    console.log('🔍 [DEBUG] itemToEdit?.id:', itemToEdit?.id);
    
    if (!companyId || !itemToEdit?.id) {
      console.error('❌ [DEBUG] companyId:', companyId, 'itemToEdit?.id:', itemToEdit?.id);
      setError("Ürün güncellenemedi. Lütfen sayfayı yenileyin.");
      return;
    }

    // Validasyon
    if (!itemName.trim() || vatRate === '' || vatStatus === '' ||
        (typeof salePrice === 'string' ? salePrice === '' : salePrice === 0)) {
      setError("Ürün adı, KDV oranı, KDV durumu ve satış fiyatı zorunlu alanlardır.");
      return;
    }

    const unitError = unitsEditorRef.current?.validate() ?? null;
    if (unitError) {
      setError(unitError);
      return;
    }

    setLoading(true);
    const packagingConflict = await unitsEditorRef.current?.preflight(itemToEdit.id) ?? null;
    if (packagingConflict) {
      setError(packagingConflict);
      setLoading(false);
      return;
    }

    try {
      // Güncellenecek DTO'yu hazırla
      const updateDto: any = {
        id: itemToEdit.id,
        companyId: companyId,
        name: itemName.trim(),
        barcode: barcode || undefined,
        isDomesticProduct,
        countryOfOrigin: countryOfOrigin || undefined,
        isScale,
        // Tartı işareti kalktıysa kod da düşer; aksi halde ürün tartıda değilken
        // barkodu hâlâ bir koda bağlı kalır
        scaleCode: isScale ? (scaleCode || '') : '',
        shelfLifeDays: (isScale || lotTrackingEnabled) ? (parseInt(shelfLifeDays, 10) || 0) : 0,
        ...(lotTrackingEnabled ? { trackLots, trackExpiry: trackLots && trackExpiry } : {}),
        stockCode: stockCode,
        categoryId: categoryId || undefined,
        brandId: brandId || undefined,
        canBeNegative,
        ...(productionEnabled ? { itemClass, productionMode } : {}),
        isVariantParent,
        // Varyant bağı korunur: null göndermek çocuk ürünü ana üründen koparıyordu.
        parentItemId: itemToEdit?.parentItemId ?? itemToEdit?.ParentItemId ?? null,
        variantGroupId: itemToEdit?.variantGroupId ?? itemToEdit?.VariantGroupId ?? null,
        unit,
        
        // Fiyat bilgileri
        purchasePrice: typeof purchasePrice === 'string' ? 
          (purchasePrice === '' ? undefined : Number(purchasePrice)) : 
          (purchasePrice || undefined),
        purchaseCurrency: purchaseCurrency || 'TRY',
        purchaseVendor: purchaseVendor || undefined,
        purchasePrice2: typeof purchasePrice2 === 'string' ? 
          (purchasePrice2 === '' ? undefined : parseFloat(purchasePrice2)) : 
          (purchasePrice2 || undefined),
        purchaseCurrency2: purchaseCurrency2 || 'TRY',
        purchaseVendor2: purchaseVendor2 || undefined,
        purchasePrice3: typeof purchasePrice3 === 'string' ? 
          (purchasePrice3 === '' ? undefined : parseFloat(purchasePrice3)) : 
          (purchasePrice3 || undefined),
        purchaseCurrency3: purchaseCurrency3 || 'TRY',
        purchaseVendor3: purchaseVendor3 || undefined,
        salePrice: typeof salePrice === 'string' ? 
          (salePrice === '' ? 0 : parseFloat(salePrice)) : 
          salePrice,
        saleCurrency: saleCurrency || 'TRY',
        saleCustomer: saleCustomer || undefined,
        salePrice2: typeof salePrice2 === 'string' ? 
          (salePrice2 === '' ? undefined : parseFloat(salePrice2)) : 
          (salePrice2 || undefined),
        saleCurrency2: saleCurrency2 || 'TRY',
        saleCustomer2: saleCustomer2 || undefined,
        salePrice3: typeof salePrice3 === 'string' ? 
          (salePrice3 === '' ? undefined : parseFloat(salePrice3)) : 
          (salePrice3 || undefined),
        saleCurrency3: saleCurrency3 || 'TRY',
        saleCustomer3: saleCustomer3 || undefined,
        listPrice: typeof listPrice === 'string' ? 
          (listPrice === '' ? undefined : parseFloat(listPrice)) : 
          (listPrice || undefined),
        listCurrency: listCurrency || 'TRY',
        
        // Vergi bilgileri
        vatRate: typeof vatRate === 'string' ? 
          (vatRate === '' ? 0 : Number(vatRate)) : 
          vatRate,
        vatStatus: vatStatus || 'excluded',
        sctRate: typeof sctRate === 'string' ? 
          (sctRate === '' ? undefined : Number(sctRate)) : 
          (sctRate || undefined),
        sctStatus: sctStatus || undefined,
        
        // Diğer bilgiler
        description: description || undefined,
        desi: variantDesi === '' ? undefined : Number(variantDesi),
        branchesId: branchesId || undefined,
        departmentNo: departmentNo === '' ? undefined : Number(departmentNo),
        itemType: itemType,
        isActive,
        
        // Çoklu resim desteği: ilk görsel kapak, tamamı imageUrls listesi.
        imageUrl: imageUrls[0] || null,
        imageUrls: imageUrls.length > 0 ? imageUrls : null,
      };

      console.log('📝 Ürün güncelleniyor:', updateDto);
      console.log('🏷️ [UPDATE-ITEM] itemType state:', itemType, '| DTO itemType:', updateDto.itemType);
      
      // Update endpoint'i çağır
      const updatedResult = await InventoryService.updateItem(companyId, itemToEdit.id, updateDto);

      // Depo/lokasyon stok kaydini da editte tutarli sekilde guncelle.
      const targetWarehouseId = warehouseId;
      const targetQuantity = quantity === '' ? 0 : Number(quantity);
      const targetLocationId = selectedLocationId || undefined;
      const sourceStocks = Array.isArray(itemToEdit?.warehouseStocks) ? itemToEdit.warehouseStocks : [];
      const sourcePrimaryStock =
        sourceStocks
          .filter((s: any) => Number(s.quantity ?? 0) > 0)
          .sort((a: any, b: any) => Number(b.quantity ?? 0) - Number(a.quantity ?? 0))[0] ||
        sourceStocks[0];
      const sourceWarehouseId = sourcePrimaryStock?.warehouseId || null;
      const sourceLocationId = sourcePrimaryStock?.locationId || undefined;

      if (targetWarehouseId && targetQuantity >= 0) {
        if (isSmartWh && targetLocationId) {
          // Deploy bagimsiz yol:
          // 1) hedef depodaki "genel/legacy" kaydi sifirla
          // 2) secili lokasyona mutlak miktari addStock ile yaz
          await InventoryService.updateWarehouseStockQuantity(
            companyId,
            itemToEdit.id,
            targetWarehouseId,
            0
          );

          if (sourceWarehouseId && sourceWarehouseId !== targetWarehouseId) {
            await InventoryService.updateWarehouseStockQuantity(
              companyId,
              itemToEdit.id,
              sourceWarehouseId,
              0
            );
          }

          if (targetQuantity > 0) {
            await InventoryService.addStockToWarehouse({
              companyId,
              productId: itemToEdit.id,
              warehouseId: targetWarehouseId,
              quantity: targetQuantity,
              locationId: targetLocationId,
            });
          }
        } else {
          // Normal depo veya lokasyonsuz senaryo
          await InventoryService.updateWarehouseStockQuantity(
            companyId,
            itemToEdit.id,
            targetWarehouseId,
            targetQuantity
          );

          if (sourceWarehouseId && sourceWarehouseId !== targetWarehouseId) {
            await InventoryService.updateWarehouseStockQuantity(
              companyId,
              itemToEdit.id,
              sourceWarehouseId,
              0
            );
          }
        }
      }

      // Ek özellikler (toggle kapalıysa mevcut kayıtları temizle)
      const featurePayload: SaveInventoryItemFeatureValueRowDto[] = itemFeatureValues
            .filter(row => row.featureId)
            .map((row, index) => ({
              featureId: row.featureId,
              value: row.value || null,
              orderIndex: index,
            }));

      await ProductFeatureService.saveValuesForItem(itemToEdit.id, featurePayload);

      // Listeyi aninda guncellemek icin parent'a normalize edilmis item gonder.
      // Otorite backend response'udur; isMainItem turevsel alandir.
      if (onItemUpdated) {
        const normalizedUpdatedItem: InventoryItem = {
          ...(itemToEdit as InventoryItem),
          ...updatedResult,
          parentItemId: updatedResult?.parentItemId ?? null,
          isVariantParent: updatedResult?.isVariantParent === true,
          isMainItem: !updatedResult?.parentItemId
        };
        onItemUpdated(normalizedUpdatedItem);
      }

      try {
        const warnings = await unitsEditorRef.current?.save() ?? [];
        if (warnings.length > 0) {
          setUnitNotice({ message: warnings.join(' '), severity: 'info' });
        }
      } catch (unitSaveError: unknown) {
        const message = unitSaveError instanceof Error ? unitSaveError.message : '';
        const text = unitSaveError instanceof PackagingError && (unitSaveError.code === 'UNIT_IN_USE' || unitSaveError.code === 'BARCODE_CONFLICT')
          ? message
          : t('stock.packaging.saveFailedAfterItem', { ns: 'accounting', message, defaultValue: 'Kart kaydedildi ama ambalajlar kaydedilemedi: {{message}}' });
        setError(text);
        setUnitNotice({ message: text, severity: 'error' });
        setLoading(false);
        return;
      }

      console.log('✅ Ürün başarıyla güncellendi!');
      console.log('🏷️ [UPDATE-ITEM] Güncellenen itemType:', updatedResult?.itemType, '| Beklenen:', itemType);
      setLoading(false);
      handleClose();
    } catch (error: any) {
      console.error('❌ Ürün güncellenirken hata:', error);
      const conflict = error?.response?.data?.error;
      if (conflict === 'UNIT_CHANGE_BLOCKED') {
        setError(t('units.baseUnitLocked', { ns: 'production' }));
      } else {
        setError('Ürün güncellenirken bir hata oluştu: ' + (error.response?.data?.message || error.message));
      }
      setLoading(false);
    }
  };

  const handleSave = async (keepOpenForNext: boolean = false) => {
    // ✅ Düzenleme modu kontrolü
    if (isEditMode && itemToEdit) {
      await handleUpdate();
      return;
    }
    
    // Validasyon kontrolü (Yeni ürün için)
    const quantityValue = quantity === '' ? 0 : Number(quantity);
    
    // 🔒 companyId kontrolü - null ise kaydetme
    if (!companyId) {
      console.error('❌ [ADD-ITEM] companyId null, kayıt yapılamıyor!');
      setError("Şirket bilgisi bulunamadı. Lütfen sayfayı yenileyin veya yeniden giriş yapın.");
      return;
    }
    
    // Temel validasyon: Ürün adı, KDV, KDV durumu ve satış fiyatı zorunlu
    if (!itemName.trim() || vatRate === '' || vatStatus === '' ||
        (typeof salePrice === 'string' ? salePrice === '' : salePrice === 0)) {
      setError("Ürün adı, KDV oranı, KDV durumu ve satış fiyatı zorunlu alanlardır.");
      return;
    }
    
    // Eğer miktar > 0 ise depo seçimi zorunlu
    if (quantityValue > 0 && !warehouseId) {
      setError("Miktar girildiğinde depo seçimi zorunludur. Lütfen Merkez Depo'yu seçin.");
      return;
    }

    if (quantityValue > 0 && isSmartWh && !selectedLocationId) {
      setError("Akıllı depoda lokasyon seçimi zorunludur.");
      return;
    }

    // Stok kodu otomatik oluşturma (girilmemişse)
    const inventoryCode = stockCode || `${itemName.substring(0, 2).toUpperCase()}${Math.floor(1000 + Math.random() * 9000)}`;
    
    // Barkod otomatik oluşturma (girilmemişse) — geçerli EAN-13.
    // Etiket basarken gövde 12 haneye kırpılıp kontrol hanesi yeniden
    // hesaplandığı için üretilen değerin baştan geçerli olması şart; aksi
    // halde basılan barkod üründe kayıtlı olandan farklı çıkıyor.
    const generatedBarcode = barcode && barcode.trim() !== ''
      ? barcode
      : generateInternalBarcode();

    // Barkod kontrolü yap (otomatik oluşturulmuş veya girilmiş olsun)
    setSuccessMsg(null);
    setLoading(true);
    try {
      const isDuplicate = await checkDuplicateBarcode(generatedBarcode);
      if (isDuplicate) {
        setLoading(false);
        setIsDuplicateBarcodeModalOpen(true);
        return;
      }
    } catch (error: any) {
      console.error('Barkod kontrolünde hata:', error);
      setError('Barkod kontrolü sırasında bir hata oluştu: ' + error.message);
      setLoading(false);
      return;
    }


    // ✅ Depo stok bilgisi (ürün oluşturduktan sonra ayrı eklenecek)
    const warehouseStockQuantity = quantity === '' ? 0 : Number(quantity);
    const selectedWarehouseId = warehouseId; // Null olabilir artık
    
    // SQL API için DTO oluştur
    const newItemDto: CreateInventoryItemDto = {
      companyId: companyId!,
      name: itemName.trim(),
      barcode: generatedBarcode, // ✅ Otomatik oluşturulmuş veya kullanıcı tarafından girilmiş
      isDomesticProduct,
      countryOfOrigin: countryOfOrigin || undefined,
      isScale,
      scaleCode: isScale ? (scaleCode || undefined) : undefined,
      shelfLifeDays: (isScale || lotTrackingEnabled) ? (parseInt(shelfLifeDays, 10) || undefined) : undefined,
      ...(lotTrackingEnabled ? { trackLots, trackExpiry: trackLots && trackExpiry } : {}),
      stockCode: inventoryCode,
      categoryId: categoryId || undefined,
      brandId: brandId || undefined,
      // ❌ KALDIRILDI: warehouseId ve quantity artık CreateWarehouseStockDto ile ayrı gönderilecek
      // warehouseId: warehouseId!,
      // quantity: quantity === '' ? 0 : Number(quantity),
      
      // Stok bilgileri
      canBeNegative,
      ...(productionEnabled ? { itemClass: itemClass || null, productionMode } : {}),
      isVariantParent,
      parentItemId: undefined,
      unit,
      minStockLevel: undefined,
      maxStockLevel: undefined,
      
      // Alış fiyatları
      purchasePrice: typeof purchasePrice === 'string' ? 
        (purchasePrice === '' ? undefined : Number(purchasePrice)) : 
        (purchasePrice || undefined),
      purchaseCurrency: purchaseCurrency || 'TRY',
      purchaseVendor: purchaseVendor || undefined,
      purchasePrice2: typeof purchasePrice2 === 'string' ? 
        (purchasePrice2 === '' ? undefined : Number(purchasePrice2)) : 
        (purchasePrice2 || undefined),
      purchaseCurrency2: purchaseCurrency2 || 'TRY',
      purchaseVendor2: purchaseVendor2 || undefined,
      purchasePrice3: typeof purchasePrice3 === 'string' ? 
        (purchasePrice3 === '' ? undefined : Number(purchasePrice3)) : 
        (purchasePrice3 || undefined),
      purchaseCurrency3: purchaseCurrency3 || 'TRY',
      purchaseVendor3: purchaseVendor3 || undefined,
      
      // Satış fiyatları
      salePrice: typeof salePrice === 'string' ? 
        (salePrice === '' ? 0 : parseFloat(salePrice)) : 
        salePrice,
      saleCurrency: saleCurrency || 'TRY',
      saleCustomer: saleCustomer || undefined,
      salePrice2: typeof salePrice2 === 'string' ? 
        (salePrice2 === '' ? undefined : parseFloat(salePrice2)) : 
        (salePrice2 || undefined),
      saleCurrency2: saleCurrency2 || 'TRY',
      saleCustomer2: saleCustomer2 || undefined,
      salePrice3: typeof salePrice3 === 'string' ? 
        (salePrice3 === '' ? undefined : parseFloat(salePrice3)) : 
        (salePrice3 || undefined),
      saleCurrency3: saleCurrency3 || 'TRY',
      saleCustomer3: saleCustomer3 || undefined,
      listPrice: typeof listPrice === 'string' ? 
        (listPrice === '' ? undefined : parseFloat(listPrice)) : 
        (listPrice || undefined),
      listCurrency: listCurrency || 'TRY',
      
      // Vergi bilgileri
      vatRate: typeof vatRate === 'string' ? 
        (vatRate === '' ? 0 : Number(vatRate)) : 
        vatRate,
      vatStatus: vatStatus || 'excluded',
      sctRate: typeof sctRate === 'string' ? 
        (sctRate === '' ? undefined : Number(sctRate)) : 
        (sctRate || undefined),
      sctStatus: sctStatus || undefined,
      
      // Diğer bilgiler
      description: description || undefined,
      desi: variantDesi === '' ? undefined : Number(variantDesi),
      branchesId: branchesId || undefined, // Firebase'den şube bilgisi
      departmentNo: departmentNo === '' ? undefined : Number(departmentNo),
      itemType: itemType,
      printerGroup: undefined,
      imageUrl: imageUrls[0] || undefined,
      imageUrls: imageUrls.length > 0 ? imageUrls : undefined,
      isActive
    };

    console.log('🚀 SQL API ile ürün kaydediliyor:', newItemDto);
    console.log('🏷️ [ADD-ITEM] itemType state:', itemType, '| DTO itemType:', newItemDto.itemType);
    console.log('📊 Oluşturulan Barkod:', generatedBarcode, '| Stok Kodu:', inventoryCode);
    console.log('📦 Depo Stok Bilgisi:', { warehouseId: selectedWarehouseId, quantity: warehouseStockQuantity });
    
    const newPackagingError = unitsEditorRef.current?.validate() ?? null;
    if (newPackagingError) {
      setError(newPackagingError);
      return;
    }

    setLoading(true);
    // Ambalaj barkodu başka kartta varsa kart hiç oluşturulmaz (yarım kayıt kalmasın).
    const newPackagingConflict = await unitsEditorRef.current?.preflight(null) ?? null;
    if (newPackagingConflict) {
      setError(newPackagingConflict);
      setLoading(false);
      return;
    }

    try {
      const createdItem = await InventoryService.createItem(newItemDto);
      console.log('✅ Ürün başarıyla kaydedildi!', createdItem);
      console.log('🏷️ [ADD-ITEM] Kaydedilen itemType:', createdItem.itemType, '| Beklenen:', itemType, '| Eşleşme:', createdItem.itemType === itemType);
      
      // ✅ SADECE EĞER MİKTAR > 0 İSE DEPOYA ÜRÜN EKLE
      if (warehouseStockQuantity > 0 && selectedWarehouseId) {
        try {
          await InventoryService.addStockToWarehouse({
            companyId: companyId!,
            productId: createdItem.id,
            warehouseId: selectedWarehouseId,
            quantity: warehouseStockQuantity,
            locationId: selectedLocationId || undefined,
          });
          console.log('✅ Depoya ürün eklendi:', { 
            productId: createdItem.id, 
            warehouseId: selectedWarehouseId, 
            quantity: warehouseStockQuantity 
          });
        } catch (stockError: any) {
          console.error('❌ Depoya ürün eklenirken hata:', stockError);
          // Stok kaydı oluşturulamazsa ürünü de sil
          await InventoryService.deleteItem(companyId!, createdItem.id);
          throw new Error('Depoya ürün eklenirken hata oluştu: ' + (stockError.response?.data?.message || stockError.message));
        }
      } else {
        console.log('ℹ️ Miktar 0 olduğu için depoya ürün eklenmedi. Ürün deposuz kayıt edildi.');
      }

      const featurePayload: SaveInventoryItemFeatureValueRowDto[] = itemFeatureValues
            .filter(row => row.featureId)
            .map((row, index) => ({
              featureId: row.featureId,
              value: row.value || null,
              orderIndex: index,
            }));

      await ProductFeatureService.saveValuesForItem(createdItem.id, featurePayload);

      // Ambalajlar ve barkodlar: kart oluştuktan sonra. Başarısız olursa kart kalır, kullanıcıya söylenir.
      try {
        await unitsEditorRef.current?.saveFor(createdItem.id);
      } catch (packagingError: unknown) {
        const message = packagingError instanceof Error ? packagingError.message : '';
        setUnitNotice({
          message: t('stock.packaging.saveFailedAfterCreate', {
            ns: 'accounting',
            name: createdItem.name,
            message,
            defaultValue: '"{{name}}" eklendi ama ambalajlar kaydedilemedi: {{message}} Kartı açıp Ambalaj ve barkodlar sekmesinden yeniden deneyin.',
          }),
          severity: 'error',
        });
      }

      setLoading(false);
      
      // ✅ YENİ: Ürün başarıyla eklendiyse callback çağır
      if (onItemAdded) {
        onItemAdded(createdItem.id, createdItem.name);
      }
      
      // ✅ "Kaydet ve Yeni Ekle": modalı kapatmadan, ürün adı/barkod/stok kodu
      // dışındaki tüm alanları koruyarak bir sonraki ürün için hazırla.
      if (keepOpenForNext) {
        resetForNextItem();
        setSuccessMsg(`"${createdItem.name}" başarıyla eklendi. Yeni ürün ekleyebilirsiniz.`);
      } else {
        handleClose();
      }
    } catch (error: any) {
      console.error('❌ Ürün kaydedilirken hata:', error);
      setError('Ürün kaydedilirken bir hata oluştu: ' + (error.response?.data?.message || error.message));
      setLoading(false);
    }
  };

  // Barkod çakışma modali kapatma
  const handleCloseDuplicateBarcodeModal = () => {
    setIsDuplicateBarcodeModalOpen(false);
  };

  // Barkod çakışma kontrolü (SQL API)
  const checkDuplicateBarcode = async (barcodeToCheck: string): Promise<boolean> => {
    if (!companyId || !barcodeToCheck.trim()) {
      setBarcodePackageOwner(null);
      return false;
    }

    try {
      console.log('🔍 SQL API ile barkod kontrolü yapılıyor:', barcodeToCheck);
      const existingItemData = await InventoryService.checkBarcodeExists(companyId, barcodeToCheck);
      
      if (existingItemData) {
        console.log('❌ Barkod zaten mevcut:', existingItemData);
        // SQL'den gelen veriyi frontend interface'ine dönüştür
        const mappedItem: InventoryItem = {
          id: existingItemData.id,
          name: existingItemData.name,
          stockCode: existingItemData.stockCode,
          barcode: existingItemData.barcode,
          categoryId: existingItemData.categoryId || null,
          brandId: existingItemData.brandId || null,
          // ⚠️ DEPRECATED: quantity ve warehouseId artık kullanılmıyor
          quantity: existingItemData.warehouseStocks?.reduce((sum: number, ws: any) => sum + ws.quantity, 0) || 0,
          warehouseId: existingItemData.warehouseStocks?.[0]?.warehouseId || null,
          unit: existingItemData.unit,
          purchasePrice: existingItemData.purchasePrice || 0,
          purchaseCurrency: existingItemData.purchaseCurrency,
          purchasePrice2: existingItemData.purchasePrice2 || 0,
          purchaseCurrency2: existingItemData.purchaseCurrency2,
          purchasePrice3: existingItemData.purchasePrice3 || 0,
          purchaseCurrency3: existingItemData.purchaseCurrency3,
          purchaseVendor: existingItemData.purchaseVendor || '',
          purchaseVendor2: existingItemData.purchaseVendor2 || '',
          purchaseVendor3: existingItemData.purchaseVendor3 || '',
          salePrice: existingItemData.salePrice,
          saleCurrency: existingItemData.saleCurrency,
          salePrice2: existingItemData.salePrice2 || 0,
          saleCurrency2: existingItemData.saleCurrency2,
          salePrice3: existingItemData.salePrice3 || 0,
          saleCurrency3: existingItemData.saleCurrency3,
          saleCustomer: existingItemData.saleCustomer || '',
          saleCustomer2: existingItemData.saleCustomer2 || '',
          saleCustomer3: existingItemData.saleCustomer3 || '',
          vatRate: existingItemData.vatRate,
          vatStatus: existingItemData.vatStatus,
          sctRate: existingItemData.sctRate,
          sctStatus: existingItemData.sctStatus,
          description: existingItemData.description,
          isActive: existingItemData.isActive,
          createdAt: existingItemData.createdAt,
          branchesId: null,
          parentItemId: null,
          // LEGACY UYUM: parent bagi olmayan kayit main olarak turetilir.
          isMainItem: !existingItemData.parentItemId,
          isVariantParent: existingItemData.isVariantParent ?? false,
          departmentNo: null,
          // ✅ YENİ SİSTEM: WarehouseStocks
          warehouseStocks: existingItemData.warehouseStocks || []
        };
        
        setExistingItem(mappedItem);
        setBarcodeExists(true);
        return true;
      }
      
      console.log('✅ Barkod uygun, kullanılabilir');
      setBarcodeExists(false);
      setExistingItem(null);
      // Ana barkod başka bir kartın koli/paket ya da ek barkodu olabilir.
      try {
        const [resolved] = await resolveBarcodes([barcodeToCheck.trim()]);
        const ownId = itemToEdit?.id?.toLowerCase();
        const owner = resolved?.matches.find((m) => !m.isMainBarcode && m.itemId.toLowerCase() !== ownId);
        setBarcodePackageOwner(owner ? describeConflict(barcodeToCheck.trim(), owner) : null);
      } catch {
        setBarcodePackageOwner(null);
      }
      return false;
    } catch (error) {
      console.error('❌ SQL API barkod kontrolü hatası:', error);
      throw error;
    }
  };

  // Dialog kapanma işleyicisi
  const handleClose = () => {
    // Form alanlarını sıfırla
    setSuccessMsg(null);
    setItemName('');
    setBarcode('');
    setStockCode('');
    setIsScale(false);
    setScaleCode('');
    setShelfLifeDays('');
    setTrackLots(false);
    setTrackExpiry(false);
    setCategoryId(null);
    setBrandId(null);
    setQuantity('');
    setCanBeNegative(false);
    setItemClass('');
    setProductionMode('SALE_EXPLODE');
    setIsVariantParent(false);
    setUnit('adet');
    setWarehouseId(null);
    setImageUrls([]);
    setSelectedLocationId(null);
    setLocationOpts([]);
    setPurchasePrice('');
    setPurchaseCurrency('TRY');
    setPurchaseVendor('');
    setPurchasePrice2('');
    setPurchaseCurrency2('TRY');
    setPurchaseVendor2('');
    setPurchasePrice3('');
    setPurchaseCurrency3('TRY');
    setPurchaseVendor3('');
    setSalePrice('');
    setSaleCurrency('TRY');
    setSaleCustomer('');
    setListPrice('');
    setListCurrency('TRY');
    setSalePrice2('');
    setSaleCurrency2('TRY');
    setSaleCustomer2('');
    setSalePrice3('');
    setSaleCurrency3('TRY');
    setSaleCustomer3('');
    setVatRate('');
    setVatStatus('');
    setSctRate('');
    setSctStatus('excluded');
    setDescription('');
    setSelectedCategory(null);
    setSelectedBrand(null);
    setSelectedWarehouse(null);
    setSelectedBranch(null);
    setBranchesId(null);
    setError(null);
    setBarcodeExists(false);
    setBarcodePackageOwner(null);
    setExistingItem(null);
    setIsDuplicateBarcodeModalOpen(false);
    setDepartmentNo('');
    setItemType('PRODUCT');
    setIsActive(true);
    setHasExtraFeatures(false);
    setItemFeatureValues([]);
    
    setVariantLength('');
    setVariantWidth('');
    setVariantHeight('');
    setVariantDesi('');
    setShowDesiCalculator(false);
    
    // Modalı kapat
    onClose();
  };

  // ✅ "Kaydet ve Yeni Ekle" için yumuşak sıfırlama:
  // Ürünü kaydettikten sonra modalı KAPATMADAN, benzersiz olması gereken
  // alanları (ürün adı, barkod, stok kodu, görsel) temizler; diğer tüm
  // özellikleri (kategori, marka, birim, depo, fiyatlar, KDV/ÖTV, departman,
  // şube, miktar, varyant özellikleri vb.) state'te bırakır ki benzer ürünleri
  // arka arkaya hızlıca eklemek kolay olsun.
  const resetForNextItem = () => {
    setItemName('');
    // Barkod ve stok kodu benzersiz olmalı; boş bırakılırsa bir sonraki
    // kayıtta otomatik üretilir. Korunursa "barkod çakışması" hatası verir.
    setBarcode('');
    setStockCode('');
    // Tartı kodu da benzersiz; bayrak korunuyor çünkü arka arkaya tartılı ürün
    // girmek yaygın, aynı kodu tekrar kaydetmek değil
    setScaleCode('');
    setImageUrls([]);
    // Hata/çakışma durumlarını temizle
    setError(null);
    setBarcodeExists(false);
    setBarcodePackageOwner(null);
    setExistingItem(null);
    setIsDuplicateBarcodeModalOpen(false);
  };

  const handleCalculateDesi = () => {
    if (variantLength === '' || variantWidth === '' || variantHeight === '') {
      // Kullanıcıyı genel hata ile boğmak yerine mini hesap alanını aç.
      setShowDesiCalculator(true);
      return;
    }

    // Desi hesaplamasi cm tabanlidir: (Boy x En x Yukseklik) / 3000
    const lengthCm = Number(variantLength);
    const widthCm = Number(variantWidth);
    const heightCm = Number(variantHeight);
    const calculatedDesi = (lengthCm * widthCm * heightCm) / 3000;

    setVariantDesi(Number(calculatedDesi.toFixed(3)));
    setShowDesiCalculator(true);
    setError(null);
  };

  const handleToggleDesiCalculator = () => {
    setShowDesiCalculator(prev => !prev);
  };

  // Para birimi sembolünü döndür
  const getCurrencySymbol = (code: string): string => {
    switch(code) {
      case 'TRY': return '₺';
      case 'USD': return '$';
      case 'EUR': return '€';
      case 'GBP': return '£';
      case 'JPY': return '¥';
      case 'CHF': return 'Fr.';
      case 'AUD': return 'A$';
      case 'CAD': return 'C$';
      case 'CNY': return '¥';
      default: return code;
    }
  };

  const handleRemoveImageUrl = (url: string) => {
    setImageUrls(prev => prev.filter(x => x !== url));
  };

  const handleAddFeatureRow = () => {
    setItemFeatureValues(prev => ([
      ...prev,
      {
        tempId: `${Date.now()}-${prev.length}`,
        featureId: '',
        value: '',
      },
    ]));
  };

  const handleUpdateFeatureRow = (tempId: string, patch: Partial<ExtraFeatureRow>) => {
    setItemFeatureValues(prev => prev.map(row => (
      row.tempId === tempId ? { ...row, ...patch } : row
    )));
  };

  const handleRemoveFeatureRow = (tempId: string) => {
    setItemFeatureValues(prev => prev.filter(row => row.tempId !== tempId));
  };

  // Kategori seçimi
  const handleCategoryChange = (value: InventoryCategory | null) => {
    setSelectedCategory(value);
    setCategoryId(value ? value.id : null);
  };

  // Lokasyon seçimi (akıllı depo)
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null);
  const [locationOpts, setLocationOpts] = useState<{ id: string; code: string; name: string }[]>([]);
  const isSmartWh = selectedWarehouse?.warehouseType === 'Smart';
  const locationEmptyForSmart = isSmartWh && locationOpts.length === 0;

  const loadWarehouseLocations = async (warehouseId: string, preserveLocationId?: string | null) => {
    try {
      const r = await accountingApi.get(ACCOUNTING_API.SWL_BY_WAREHOUSE(warehouseId));
      const candidateArrays = [
        r.data?.Data,
        r.data?.data,
        r.data?.Items,
        r.data?.items,
        r.data?.result,
        r.data?.Result,
        r.data?.payload?.data,
        r.data?.payload?.Data,
      ];
      const raw =
        candidateArrays.find((arr) => Array.isArray(arr)) ||
        [];
      const allLocations = (Array.isArray(raw) ? raw : [])
        .map((l: any) => ({
          id: l.id || l.Id,
          code: l.code || l.Code || l.locationCode || l.LocationCode,
          name: l.name || l.Name || l.locationName || l.LocationName,
          type: l.type || l.Type,
          isActive: l.isActive ?? l.IsActive ?? true,
          isPickable: l.isPickable ?? l.IsPickable ?? false,
        }))
        .filter((l) => !!l.id);

      const active = allLocations.filter((l) => l.isActive);
      const pickable = active.filter((l) => l.isPickable);
      const leafTypes = new Set(['bin', 'level', 'rack']);
      const activeLeafLocations = active.filter((l) =>
        leafTypes.has(String(l.type || '').toLowerCase())
      );

      const resolved = pickable.length > 0
        ? pickable
        : (activeLeafLocations.length > 0 ? activeLeafLocations : active);

      const fallbackResolved = resolved.length > 0 ? resolved : allLocations;

      // Çok SKU / çok lokasyon modeli:
      // Akıllı depoda dolu raflar da seçilebilir olmalı.
      // Bu yüzden "sadece boş raf" filtresi kaldırıldı.
      try {
        const stockResp = await InventoryService.getStockByLocation(warehouseId, { page: 1, pageSize: 10000 });
        const stockRows = stockResp?.data || stockResp?.Data || [];

        if (Array.isArray(stockRows)) {
          // Eski davranışta stok satırları dolu raf filtrelemesi için kullanılıyordu.
          // Yeni modelde dolu/boş tüm raflar seçilebilir olduğundan sadece bağlantı
          // kontrolü amacıyla çağrı korunur, içerik filtrede kullanılmaz.
          void stockRows.length;
        }
      } catch {
        // Stok doluluk sorgusu basarisiz olursa, lokasyon listesini oldugu gibi goster.
      }

      const list = fallbackResolved.map(({ id, code, name }) => ({
        id,
        code: code || 'LOC',
        name: name || 'Lokasyon'
      }));

      // Düzenleme modunda mevcut lokasyon filtreye takilsa bile listede gorunsun.
      if (preserveLocationId && !list.some((l) => l.id === preserveLocationId)) {
        const existing = allLocations.find((l) => l.id === preserveLocationId);
        if (existing) {
          list.push({
            id: existing.id,
            code: existing.code || 'LOC',
            name: existing.name || 'Lokasyon'
          });
        }
      }

      setLocationOpts(list);
    } catch {
      setLocationOpts([]);
    }
  };

  // Edit acilisinda da secili smart deponun lokasyonlarini otomatik yukle.
  useEffect(() => {
    if (!open) return;
    if (!selectedWarehouse || selectedWarehouse.warehouseType !== 'Smart') {
      setLocationOpts([]);
      return;
    }

    loadWarehouseLocations(selectedWarehouse.id, selectedLocationId);
  }, [open, selectedWarehouse?.id, selectedWarehouse?.warehouseType]);

  // Depo seçimi
  const handleWarehouseChange = (value: Warehouse | null) => {
    setSelectedWarehouse(value);
    setWarehouseId(value ? value.id : null);
    setSelectedLocationId(null);
    if (!value || value.warehouseType !== 'Smart') {
      setLocationOpts([]);
    }
  };

  // Marka seçimi
  const handleBrandChange = (value: Brand | null) => {
    setSelectedBrand(value);
    setBrandId(value ? value.id : null);
  };

  // Modal açılış-kapanış fonksiyonları
  const handleOpenCategoryModal = () => {
    setIsCategoryModalOpen(true);
  };

  const handleCloseCategoryModal = () => {
    setIsCategoryModalOpen(false);
    // Kategori modalı kapandığında, kategorileri yeniden yükle
    if (companyId) {
      loadCategories(companyId);
    }
  };

  const handleOpenBrandModal = () => {
    setIsBrandModalOpen(true);
  };

  const handleCloseBrandModal = () => {
    setIsBrandModalOpen(false);
    // Marka modalı kapandığında, markaları yeniden yükle
    if (companyId) {
      loadBrands(companyId);
    }
  };

  const handleOpenWarehouseModal = () => {
    setIsWarehouseModalOpen(true);
  };

  const handleCloseWarehouseModal = () => {
    setIsWarehouseModalOpen(false);
    // Depo modalı kapandığında, depoları yeniden yükle
    if (companyId) {
      loadWarehouses(companyId);
    }
  };

  // Kategori tam yolunu göster
  const getCategoryFullPath = (category: InventoryCategory | null): string => {
    if (!category) return '';
    
    const parts: string[] = [category.name];
    let currentParentId = category.parentId;
    
    while (currentParentId) {
      const parentCategory = categories.find(c => c.id === currentParentId);
      if (parentCategory) {
        parts.unshift(parentCategory.name);
        currentParentId = parentCategory.parentId;
      } else {
        break;
      }
    }
    
    return parts.join(' > ');
  };

  // Kategori filtresini geliştirme
  // Kategori tam yolunu almak için bir yardımcı fonksiyon ekleyelim
  const getFullCategoryPath = (categoryId: string): string => {
    const category = categories.find(cat => cat.id === categoryId);
    if (!category) return '';
    
    let path = category.name;
    let currentCat = category;
    
    while (currentCat.parentId) {
      const parentCat = categories.find(cat => cat.id === currentCat.parentId);
      if (!parentCat) break;
      path = `${parentCat.name} > ${path}`;
      currentCat = parentCat;
    }
    
    return path;
  };

  // Filtrelenmiş kategori seçeneklerini hazırlayalım
  const getFilteredCategoryOptions = (): (InventoryCategory & { label: string; isParent: boolean; level: number })[] => {
    return categories.map(category => ({
      ...category,
      name: getFullCategoryPath(category.id),
      label: getFullCategoryPath(category.id),
      level: calculateCategoryLevel(category),
      isParent: categories.some(cat => cat.parentId === category.id)
    }));
  };

  // Kategori seviyesini hesaplamak için
  const calculateCategoryLevel = (category: InventoryCategory): number => {
    let level = 1;
    let currentCat = category;
    
    while (currentCat.parentId) {
      level++;
      const parentCat = categories.find(cat => cat.id === currentCat.parentId);
      if (!parentCat) break;
      currentCat = parentCat;
    }
    
    return level;
  };
  return {
    open, itemToEdit, branchId, onClose,
    isEditMode, productionEnabled, lotTrackingEnabled,
    successMsg,
    setSuccessMsg,
    unitNotice,
    setUnitNotice,
    baseUnitLocked,
    setBaseUnitLocked,
    itemName,
    setItemName,
    barcode,
    setBarcode,
    isDomesticProduct,
    setIsDomesticProduct,
    countryOfOrigin,
    setCountryOfOrigin,
    isScale,
    setIsScale,
    scaleCode,
    setScaleCode,
    shelfLifeDays,
    setShelfLifeDays,
    trackLots,
    setTrackLots,
    trackExpiry,
    setTrackExpiry,
    stockCode,
    setStockCode,
    categoryId,
    setCategoryId,
    brandId,
    setBrandId,
    quantity,
    setQuantity,
    canBeNegative,
    setCanBeNegative,
    isVariantParent,
    setIsVariantParent,
    unit,
    setUnit,
    warehouseId,
    setWarehouseId,
    imageUrls,
    setImageUrls,
    purchasePrice,
    setPurchasePrice,
    purchaseCurrency,
    setPurchaseCurrency,
    purchaseVendor,
    setPurchaseVendor,
    purchasePrice2,
    setPurchasePrice2,
    purchaseCurrency2,
    setPurchaseCurrency2,
    purchaseVendor2,
    setPurchaseVendor2,
    purchasePrice3,
    setPurchasePrice3,
    purchaseCurrency3,
    setPurchaseCurrency3,
    purchaseVendor3,
    setPurchaseVendor3,
    salePrice,
    setSalePrice,
    saleCurrency,
    setSaleCurrency,
    saleCustomer,
    setSaleCustomer,
    listPrice,
    setListPrice,
    listCurrency,
    setListCurrency,
    salePrice2,
    setSalePrice2,
    saleCurrency2,
    setSaleCurrency2,
    saleCustomer2,
    setSaleCustomer2,
    salePrice3,
    setSalePrice3,
    saleCurrency3,
    setSaleCurrency3,
    saleCustomer3,
    setSaleCustomer3,
    vatRate,
    setVatRate,
    vatStatus,
    setVatStatus,
    sctRate,
    setSctRate,
    sctStatus,
    setSctStatus,
    description,
    setDescription,
    departmentNo,
    setDepartmentNo,
    itemType,
    setItemType,
    isActive,
    setIsActive,
    itemClass,
    setItemClass,
    productionMode,
    setProductionMode,
    variantLength,
    setVariantLength,
    variantWidth,
    setVariantWidth,
    variantHeight,
    setVariantHeight,
    variantDesi,
    setVariantDesi,
    showDesiCalculator,
    setShowDesiCalculator,
    selectedBranch,
    setSelectedBranch,
    branchesId,
    setBranchesId,
    categories,
    setCategories,
    warehouses,
    setWarehouses,
    brands,
    setBrands,
    selectedCategory,
    setSelectedCategory,
    selectedWarehouse,
    setSelectedWarehouse,
    selectedBrand,
    setSelectedBrand,
    loading,
    setLoading,
    initialLoading,
    setInitialLoading,
    error,
    setError,
    companyId,
    setCompanyId,
    showInfoBox,
    setShowInfoBox,
    availableCurrencies,
    setAvailableCurrencies,
    isCategoryModalOpen,
    setIsCategoryModalOpen,
    isBrandModalOpen,
    setIsBrandModalOpen,
    isWarehouseModalOpen,
    setIsWarehouseModalOpen,
    isImageLibraryModalOpen,
    setIsImageLibraryModalOpen,
    hasExtraFeatures,
    setHasExtraFeatures,
    availableFeatures,
    setAvailableFeatures,
    itemFeatureValues,
    setItemFeatureValues,
    barcodeExists,
    setBarcodeExists,
    barcodePackageOwner,
    existingItem,
    setExistingItem,
    isDuplicateBarcodeModalOpen,
    setIsDuplicateBarcodeModalOpen,
    selectedLocationId,
    setSelectedLocationId,
    locationOpts,
    setLocationOpts,
    branches,
    unitsEditorRef,
    shelfLifeDaysHelperText,
    scaleCodeHelperText,
    unitOptions,
    UNIT_TRANSLATION_KEYS,
    getUnitLabel,
    handleBranchChange,
    getBranchName,
    loadCategories,
    loadWarehouses,
    loadBrands,
    loadCurrencies,
    handleUpdate,
    handleSave,
    handleCloseDuplicateBarcodeModal,
    checkDuplicateBarcode,
    handleClose,
    resetForNextItem,
    handleCalculateDesi,
    handleToggleDesiCalculator,
    getCurrencySymbol,
    handleRemoveImageUrl,
    handleAddFeatureRow,
    handleUpdateFeatureRow,
    handleRemoveFeatureRow,
    handleCategoryChange,
    isSmartWh,
    locationEmptyForSmart,
    loadWarehouseLocations,
    handleWarehouseChange,
    handleBrandChange,
    handleOpenCategoryModal,
    handleCloseCategoryModal,
    handleOpenBrandModal,
    handleCloseBrandModal,
    handleOpenWarehouseModal,
    handleCloseWarehouseModal,
    getCategoryFullPath,
    getFullCategoryPath,
    getFilteredCategoryOptions,
    calculateCategoryLevel,
    t,
    globalBranches,
    userDetails,
    isModuleEnabled,
  };
}

export type StockCardForm = ReturnType<typeof useStockCardForm>;
